// FeedbackValidator - Validates AI feedback quality to prevent generic or incorrect suggestions
// Ensures feedback respects intentional stylistic choices and maintains authentic voice

class FeedbackValidator {
  // Main validation function for a single annotation
  static validateFeedbackQuality(annotation, essayText) {
    const issues = [];
    
    // Check for repetition that might be intentional
    if (this.mightBeIntentionalRepetition(annotation)) {
      issues.push({
        type: 'intentional_repetition',
        severity: 'warning',
        message: 'Possible intentional repetition for parallel structure or emphasis'
      });
    }
    
    // Check for specificity accuracy
    const specificityIssue = this.checkSpecificityAccuracy(annotation);
    if (specificityIssue) {
      issues.push(specificityIssue);
    }
    
    // Check for voice authenticity
    const voiceIssue = this.assessVoiceAuthenticity(annotation);
    if (voiceIssue) {
      issues.push(voiceIssue);
    }
    
    // Check for factual accuracy
    const accuracyIssue = this.checkFactualAccuracy(annotation);
    if (accuracyIssue) {
      issues.push(accuracyIssue);
    }
    
    return {
      annotation,
      issues,
      isValid: issues.filter(i => i.severity === 'error').length === 0,
      qualityScore: this.calculateQualityScore(issues)
    };
  }
  
  // Detect intentional repetition patterns
  static mightBeIntentionalRepetition(annotation) {
    if (!annotation.feedback) return false;
    
    const feedbackLower = annotation.feedback.toLowerCase();
    const textLower = (annotation.originalText || '').toLowerCase();
    
    // Check if feedback mentions repetition
    if (!feedbackLower.includes('repetition') && 
        !feedbackLower.includes('repeat') && 
        !feedbackLower.includes('varying') &&
        !feedbackLower.includes('avoid using')) {
      return false;
    }
    
    // Patterns that indicate intentional repetition
    const intentionalPatterns = [
      /instead of .+ i .+/i,  // "Instead of X, I Y"
      /not .+ but .+/i,       // "Not X but Y"
      /i \w+ i \w+ i \w+/i,   // "I came, I saw, I conquered"
      /first .+ then .+ finally/i,
      /some .+ others .+/i,
      /neither .+ nor .+/i,
      /both .+ and .+/i,
      /either .+ or .+/i
    ];
    
    // Check for parallel structure indicators
    for (const pattern of intentionalPatterns) {
      if (pattern.test(textLower)) {
        return true;
      }
    }
    
    // Check for anaphora (repetition at beginning of clauses)
    const sentences = textLower.split(/[.!?;,]/).map(s => s.trim());
    const firstWords = sentences.map(s => s.split(' ')[0]).filter(w => w);
    
    // If the same word starts multiple clauses, it might be intentional
    const wordCounts = {};
    firstWords.forEach(word => {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
    });
    
    const hasRepeatedStarts = Object.values(wordCounts).some(count => count >= 2);
    if (hasRepeatedStarts && sentences.length >= 2) {
      return true;
    }
    
    return false;
  }
  
  // Check if specificity feedback is accurate
  static checkSpecificityAccuracy(annotation) {
    const feedbackLower = annotation.feedback?.toLowerCase() || '';
    const textLower = (annotation.originalText || '').toLowerCase();
    
    // Check if feedback suggests making something more specific
    if (!feedbackLower.includes('specific') && 
        !feedbackLower.includes('vague') &&
        !feedbackLower.includes('timeframe') &&
        !feedbackLower.includes('concrete')) {
      return null;
    }
    
    // Time-related specificity checks
    const specificTimePatterns = [
      /\d+ (weeks?|days?|months?|years?|hours?|minutes?) ago/i,
      /last (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,
      /yesterday|today|tomorrow/i,
      /at \d+:\d+/i,
      /on (january|february|march|april|may|june|july|august|september|october|november|december) \d+/i,
      /in \d{4}/i, // specific year
      /\d+(st|nd|rd|th) grade/i,
      /age \d+/i,
      /when i was \d+/i
    ];
    
    // Check if text already contains specific time references
    for (const pattern of specificTimePatterns) {
      if (pattern.test(textLower)) {
        // Text is already specific
        if (feedbackLower.includes('more specific') || feedbackLower.includes('vague')) {
          return {
            type: 'incorrect_specificity',
            severity: 'error',
            message: 'Text already contains specific timeframe/details'
          };
        }
      }
    }
    
    // Quantity-related specificity
    const quantityPatterns = [
      /\d+\.?\d* (percent|%)/i,
      /\$\d+/i,
      /\d+ (people|students|friends|classmates|teammates)/i,
      /\d+ (times|occasions)/i,
      /\d+ (miles|kilometers|meters|feet)/i
    ];
    
    for (const pattern of quantityPatterns) {
      if (pattern.test(textLower)) {
        if (feedbackLower.includes('more specific') || feedbackLower.includes('concrete')) {
          return {
            type: 'incorrect_specificity',
            severity: 'warning',
            message: 'Text already contains specific quantities'
          };
        }
      }
    }
    
    return null;
  }
  
  // Assess if suggested changes preserve authentic voice
  static assessVoiceAuthenticity(annotation) {
    if (!annotation.suggestedText || annotation.type !== 'inline_edit') {
      return null;
    }
    
    const original = annotation.originalText?.toLowerCase() || '';
    const suggested = annotation.suggestedText?.toLowerCase() || '';
    
    // Mapping of casual to overly formal replacements
    const voiceKillers = {
      'mom': ['mother'],
      'dad': ['father'],
      'got': ['obtained', 'acquired', 'procured'],
      'get': ['obtain', 'acquire', 'procure'],
      'use': ['utilize', 'employ'],
      'start': ['commence', 'initiate'],
      'end': ['terminate', 'conclude'],
      'help': ['assist', 'aid'],
      'show': ['demonstrate', 'exhibit'],
      'tell': ['inform', 'convey'],
      'ask': ['inquire', 'request'],
      'try': ['attempt', 'endeavor'],
      'make': ['construct', 'fabricate'],
      'think': ['contemplate', 'ponder'],
      'want': ['desire', 'wish'],
      'need': ['require', 'necessitate'],
      'big': ['substantial', 'considerable'],
      'small': ['minute', 'diminutive'],
      'good': ['excellent', 'superior'],
      'bad': ['inferior', 'substandard'],
      'very': ['extremely', 'exceedingly'],
      'really': ['genuinely', 'truly'],
      'thing': ['entity', 'object'],
      'stuff': ['materials', 'items'],
      'kid': ['child', 'youth'],
      'kids': ['children', 'youths']
    };
    
    // Check if suggestion makes language unnecessarily formal
    for (const [casual, formals] of Object.entries(voiceKillers)) {
      if (original.includes(casual)) {
        for (const formal of formals) {
          if (suggested.includes(formal)) {
            return {
              type: 'voice_formalization',
              severity: 'warning',
              message: `Suggestion changes casual "${casual}" to formal "${formal}" - may lose authentic voice`
            };
          }
        }
      }
    }
    
    // Check if suggestion removes contractions (making it more formal)
    const contractions = ["don't", "won't", "can't", "wouldn't", "couldn't", "shouldn't", 
                         "isn't", "aren't", "wasn't", "weren't", "I'm", "I've", "I'll", 
                         "you're", "you've", "you'll", "we're", "we've", "we'll",
                         "they're", "they've", "they'll", "it's", "that's", "what's"];
    
    const hadContractions = contractions.some(c => original.includes(c));
    const hasContractions = contractions.some(c => suggested.includes(c));
    
    if (hadContractions && !hasContractions) {
      return {
        type: 'voice_formalization',
        severity: 'warning',
        message: 'Suggestion removes contractions, making voice more formal'
      };
    }
    
    return null;
  }
  
  // Check for factual accuracy in feedback
  static checkFactualAccuracy(annotation) {
    const feedbackLower = annotation.feedback?.toLowerCase() || '';
    
    // Common factual errors in feedback
    const factualErrors = [
      {
        pattern: /two weeks.*more specific.*last month/i,
        issue: '"Two weeks ago" is more specific than "last month"'
      },
      {
        pattern: /yesterday.*vague/i,
        issue: '"Yesterday" is already very specific'
      },
      {
        pattern: /passive voice/i,
        check: (text) => {
          // Simple check for actual passive voice
          const passiveIndicators = ['was', 'were', 'been', 'being', 'be'];
          const pastParticiples = ['ed', 'en', 'wn'];
          const hasPassiveStructure = passiveIndicators.some(ind => 
            text.toLowerCase().includes(ind)) && 
            pastParticiples.some(end => 
              text.toLowerCase().match(new RegExp(`\\w+${end}\\b`)));
          return !hasPassiveStructure;
        },
        issue: 'Incorrectly identified as passive voice'
      }
    ];
    
    for (const error of factualErrors) {
      if (error.pattern && error.pattern.test(feedbackLower)) {
        return {
          type: 'factual_error',
          severity: 'error',
          message: error.issue
        };
      }
      
      if (error.check && feedbackLower.includes('passive') && 
          error.check(annotation.originalText || '')) {
        return {
          type: 'factual_error',
          severity: 'error',
          message: error.issue
        };
      }
    }
    
    return null;
  }
  
  // Calculate overall quality score for feedback
  static calculateQualityScore(issues) {
    if (issues.length === 0) return 100;
    
    let score = 100;
    
    issues.forEach(issue => {
      switch(issue.severity) {
        case 'error':
          score -= 25;
          break;
        case 'warning':
          score -= 10;
          break;
        case 'info':
          score -= 5;
          break;
      }
    });
    
    return Math.max(0, score);
  }
  
  // Validate all annotations in a feedback response
  static validateAllAnnotations(annotations, essayText) {
    if (!annotations || !Array.isArray(annotations)) {
      return {
        valid: [],
        problematic: [],
        summary: { totalIssues: 0, avgQuality: 0 }
      };
    }
    
    const results = annotations.map(ann => 
      this.validateFeedbackQuality(ann, essayText)
    );
    
    const valid = results.filter(r => r.isValid).map(r => r.annotation);
    const problematic = results.filter(r => !r.isValid);
    
    const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);
    const avgQuality = results.reduce((sum, r) => sum + r.qualityScore, 0) / results.length;
    
    return {
      valid,
      problematic,
      allResults: results,
      summary: {
        totalAnnotations: annotations.length,
        validAnnotations: valid.length,
        problematicAnnotations: problematic.length,
        totalIssues,
        avgQuality: Math.round(avgQuality)
      }
    };
  }
  
  // Generate a quality report for debugging
  static generateQualityReport(validationResults) {
    const report = {
      timestamp: new Date().toISOString(),
      summary: validationResults.summary,
      issueBreakdown: {},
      recommendations: []
    };
    
    // Count issues by type
    validationResults.allResults.forEach(result => {
      result.issues.forEach(issue => {
        report.issueBreakdown[issue.type] = 
          (report.issueBreakdown[issue.type] || 0) + 1;
      });
    });
    
    // Generate recommendations
    if (report.issueBreakdown.intentional_repetition > 2) {
      report.recommendations.push(
        'Multiple instances of potentially intentional repetition flagged - consider adjusting detection sensitivity'
      );
    }
    
    if (report.issueBreakdown.voice_formalization > 3) {
      report.recommendations.push(
        'Frequent voice formalization detected - feedback may be making writing less authentic'
      );
    }
    
    if (report.issueBreakdown.factual_error > 0) {
      report.recommendations.push(
        'Factual errors in feedback detected - critical issue requiring immediate attention'
      );
    }
    
    return report;
  }
}

export default FeedbackValidator;